import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, combineLatestWith, Observable, Subject } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { MmuUserType } from '../../auth/models';
import { MmuUserService } from '../../auth/user-service/mmu-user.service';
import { MMU_SUBSIDIARY_COOKIE_NAME } from '../../injection-tokens/mmu-subsidiary-cookie-name.token';
import { MmuLogger } from '../../logging';
import { MmuMaintenanceNotice, MmuMaintenanceNoticeFileContent } from './mmu-maintenance-notice';
import { MMU_MAINTENANCE_NOTICE_URL } from './mmu-maintenance-notice-url';


@Injectable({
  providedIn: 'root'
})
export class MmuMaintenanceService implements OnDestroy {

  readonly closedNotices$: BehaviorSubject<string[]>;
  readonly sessionStorageKey = 'MMU_CLOSED_MAINTENANCE_BANNERS';

  private readonly destroy$ = new Subject<void>();

  constructor(
    @Inject(MMU_MAINTENANCE_NOTICE_URL) private readonly noticeUrl: string,
    @Inject(MMU_SUBSIDIARY_COOKIE_NAME) private readonly subsidiaryCookieName: string,
    private http: HttpClient,
    private userService: MmuUserService,
    private cookieService: CookieService,
    private logger: MmuLogger
  ) {
    // Initialize the list of closed notices from session storage
    this.closedNotices$ = new BehaviorSubject(this.getClosedNotices());

    // Update storage every time the list of closed notices changes
    this.closedNotices$
      .pipe(takeUntil(this.destroy$))
      .subscribe((closedNotices) => {
        if (closedNotices.length > 0) {
          sessionStorage.setItem(this.sessionStorageKey, JSON.stringify(closedNotices));
        } else {
          sessionStorage.removeItem(this.sessionStorageKey);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Loads maintenance notice from Azure blob storage. Don't apply any filters here because this is still used by the static pages!
   * @deprecated Use `getNotices` instead to receive all current notices
   */
  getNotice(): Observable<MmuMaintenanceNotice> {
    return this.http.get<MmuMaintenanceNoticeFileContent>(this.noticeUrl)
      .pipe(
        map((res) => {
          return Array.isArray(res) ? res[0] : res;
        })
      );
  }

  /**
   * Loads maintenance notice(s) from Azure blob storage. Will filter the notices and only return the ones that match the current user's
   * subsidiary (if set in the notice) and also matches the given time range. Notices that have already been closed by the user will be
   * filtered out as well.
   *
   * Automatically updates when the list of closed notices changes.
   *
   * @param filter Whether to apply the filters or not
   */
  getNotices(filter = true): Observable<MmuMaintenanceNotice[]> {
    return this.http.get<MmuMaintenanceNoticeFileContent>(this.noticeUrl)
      .pipe(
        combineLatestWith(this.closedNotices$),
        map(([res, closedNotices]) => {
          const notices = Array.isArray(res) ? res : [res];

          if (!filter) {
            return notices;
          }

          // Filter by time range
          const now = new Date().getTime();
          const currentSubsidiary = this.getCurrentSubsidiary();
          const currentUserType = this.userService.user?.type;
          return notices
            .filter((notice) => this.filterByTimeRange(now, notice))
            .filter((notice) => this.filterBySubsidiary(notice, currentSubsidiary))
            .filter((notice) => this.filterByUserType(notice, currentUserType))
            .filter((notice) => !closedNotices.includes(this.createNoticeContentHash(notice)));
        }),
        catchError(() => [])
      );
  }

  closeNotice(notice: MmuMaintenanceNotice): void {
    const hash = this.createNoticeContentHash(notice);

    const closedNotices: string[] = this.closedNotices$.getValue();
    const newClosedNotices = [...closedNotices, hash];

    this.closedNotices$.next(newClosedNotices);
  }

  private getCurrentSubsidiary(): string {
    return this.cookieService.get(this.subsidiaryCookieName);
  }

  private filterByTimeRange(now: number, { to, from }: MmuMaintenanceNotice): boolean {
    // Only show notices that are currently active
    return now >= new Date(from).getTime() && now <= new Date(to).getTime();
  }

  private filterBySubsidiary(notice: MmuMaintenanceNotice, currentSubsidiary: string): boolean {
    if (!notice.subsidiaries || notice.subsidiaries?.length === 0) {
      // When no subsidiaries are set, the notice should be shown to all users
      return true;
    }

    return notice.subsidiaries.some((s) => s.toLowerCase() === currentSubsidiary);
  }

  private filterByUserType(notice: MmuMaintenanceNotice, currentUserType: MmuUserType): boolean {
    if (!notice.userType || notice.userType?.length === 0) {
      // When no userTypes are set, the notice should be shown to all users
      return true;
    }

    return Array.isArray(notice.userType)
           ? notice.userType.some((s) => s.toLowerCase() === currentUserType.toLowerCase())
           : notice.userType.toLowerCase() === currentUserType.toLowerCase();
  }

  /**
   * Creates a hash from the notice object in order to check if the notice has changed and needs to be shown again. The hash will be
   * different depending on the notice text content.
   *
   * See: https://stackoverflow.com/a/7616484
   * @param {MmuMaintenanceNotice} notice The notice object
   * @returns {string} The hash
   * @private
   */
  private createNoticeContentHash(notice: MmuMaintenanceNotice): string {
    // Create the hash input string by concatenating all translations in a sorted order. This way the hash will be the same regardless
    // of the order of the translations in the object.
    const input = Object.entries(notice.translations)
      .sort(([langA], [langB]) => langA.localeCompare(langB))
      .reduce((
        acc,
        [_, translation]
      ) => {
        return acc + (typeof translation === 'string' ? translation : translation.content);
      }, '');

    let hash = 0;

    if (input.length === 0) {
      return hash.toString();
    }

    for (let i = 0; i < input.length; i++) {
      const chr = input.charCodeAt(i);
      hash = ((hash << 5) - hash) + chr;
      hash |= 0; // Convert to 32bit integer
    }

    return hash.toString();
  }

  private getClosedNotices(): string[] {
    try {
      const storageItem = sessionStorage.getItem(this.sessionStorageKey);

      if (!storageItem) {
        return [];
      }

      return JSON.parse(storageItem);
    } catch (e) {
      this.logger.warn('Could not parse closed notices from session storage', e);
      return [];
    }
  }
}
